import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { addDays, isAfter, isBefore, isValid, parse, subDays } from 'date-fns';
import { List } from 'immutable';
import _ from 'lodash';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { fetchMeasurements } from '@sophocles/api/measurements';
import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { selectLightFixturesAsImmutableMap } from '@sophocles/features/reference';
import { formatDateForSubmission, getDateTimeFormat, getUserMeasurementSystem } from '@sophocles/utils/formatUtils';
import {
  getUnitAbbreviation,
  isRelayMeasurement,
  getMeasurementTypeLabel,
  groupMeasurementTypes,
  getBaseMeasurementType,
  currentToPower,
  powerToCurrent,
  toCelsius,
  toFahrenheit,
  isSensorMeasurement,
} from '@sophocles/utils/measurementUtils';
import MeasurementChart from '@sophocles/views/sites/components/MeasurementChart';
import MeasurementDataTable from '@sophocles/views/sites/components/MeasurementDataTable';
import PowerMenu from '@sophocles/views/sites/components/PowerMenu';
import RelayInfoCard from '@sophocles/views/sites/components/RelayInfoCard';
import { buildRelayDisplayData } from '@sophocles/views/sites/components/relayUtils';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

// Per-type row budget for the combined measurements fetch. The endpoint's page
// limit now caps the whole type__in set, so the request scales this by the
// number of requested types to keep each type's prior coverage.
const MEASUREMENT_PAGE_LIMIT_PER_TYPE = 1000;

function MeasurementDataView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useUser();
  const { selectedStructure: site, customer, loading: dashboardLoading } = useSiteDashboardLayoutContext();

  const [loading, setLoading] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const initialFromRaw = queryParams.get('from');
  const initialToRaw = queryParams.get('to');

  // Capture whether URL had date params on initial mount (before effects write to URL)
  const [hadInitialDateParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('from') || params.has('to');
  });
  // Parse and validate the dates
  const initialFrom = initialFromRaw ? parse(initialFromRaw, 'yyyy-MM-dd', new Date()) : subDays(new Date(), 30);
  const initialTo = initialToRaw ? parse(initialToRaw, 'yyyy-MM-dd', new Date()) : addDays(new Date(), 1);
  const [from, setFrom] = useState(isValid(initialFrom) ? initialFrom : subDays(new Date(), 30));
  const [to, setTo] = useState(isValid(initialTo) ? initialTo : addDays(new Date(), 1));

  // const [measurementType, setMeasurementType] = useState('all');
  const [measurements, setMeasurements] = useState([]);
  const [availableMeasurementTypes, setAvailableMeasurementTypes] = useState([]);
  const [measurementGroups, setMeasurementGroups] = useState({});
  const [displayUnit, setDisplayUnit] = useState('power'); // 'power' or 'current'

  // Set temperature unit based on user's measurement system preference
  const userMeasurementSystem = getUserMeasurementSystem(user);
  const [temperatureUnit, setTemperatureUnit] = useState(
    userMeasurementSystem === 'imperial' ? 'fahrenheit' : 'celsius',
  );

  const timeZone = site.get('time_zone', null);
  const unitId = site.getIn(['relay_controller', 'id'], null);
  const numberOfRelays = site.getIn(['relay_controller', 'number_of_relays'], 2);
  const relays = site.getIn(['relay_controller', 'relays'], List());
  const fixtures = useSelector(selectLightFixturesAsImmutableMap);

  // Relay pairing and load info
  const isBillboard = customer?.get('is_billboard', true) ?? true;
  const { relayInfo, displayRelays, expectedReadings } = buildRelayDisplayData(relays, numberOfRelays, t);

  const [tab, setTab] = useState('');
  const [groupMode, setGroupMode] = useState('all');

  const processMeasurementData = measurement => {
    const processedTimestamp = new Date(measurement.timestamp);

    // Prefer adjusted_value from API (already voltage-adjusted to watts) over raw value
    const rawValue =
      measurement.adjusted_value != null
        ? parseFloat(measurement.adjusted_value) || 0
        : parseFloat(measurement.value) || 0;

    const measurementData = {
      id: measurement.id,
      timestamp: processedTimestamp,
      // Generate datetime_timestamp if it's undefined - use ISO string for consistency
      datetime_timestamp: measurement.datetime_timestamp || processedTimestamp.toISOString(),
      // Add short_timestamp for chart display
      short_timestamp: processedTimestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      type: measurement.type,
      value: rawValue,
      unit: measurement.unit,
      discriminator: measurement.discriminator,
      // Voltage adjustment metadata from API
      source_voltage: measurement.source_voltage ?? null,
      target_voltage: measurement.target_voltage ?? null,
    };

    let processedData = { ...measurementData };

    // Helper function to convert any measurement to watts
    const convertToWatts = (value, unit, voltage = 120.0) => {
      const numValue = parseFloat(value) || 0;
      switch (unit) {
        case 'watts':
          return numValue;
        case 'kilowatts':
          return numValue * 1000.0;
        case 'milliwatts':
          return numValue / 1000.0;
        case 'amps':
          return numValue * voltage;
        case 'milliamps':
          return (numValue / 1000.0) * voltage;
        default:
          return numValue;
      }
    };

    // For relay measurements (power or current), discriminator refers to relay number
    if (measurementData.type && isRelayMeasurement(measurementData.type) && measurementData.discriminator) {
      const relayNumber = parseInt(measurementData.discriminator, 10);

      // Find the relay object by relay number
      const relay = relays.find(r => r.get('relay_number') === relayNumber);

      if (relay) {
        const relayVoltage = relay.get('voltage', 120.0) ?? 120.0;
        // Use API target_voltage when available (server-side adjustment), otherwise relay voltage
        const effectiveVoltage = measurementData.target_voltage ?? relayVoltage;
        processedData.relay = {
          relay_number: relay.get('relay_number'),
          voltage: effectiveVoltage,
          name: relay.get('name', t('sites.relayNumber', 'Relay {{number}}', { number: relayNumber })),
        };

        const isCurrentMeasurement = measurementData.type.endsWith('_current');
        const isPowerMeasurement = measurementData.type.endsWith('_power');

        if (isCurrentMeasurement) {
          // If adjusted_value was provided AND the API performed voltage adjustment (target_voltage set),
          // it's already in watts. Otherwise, convert using relay voltage.
          const apiAdjusted = measurement.adjusted_value != null && measurementData.target_voltage != null;
          const wattsValue = apiAdjusted
            ? measurementData.value
            : convertToWatts(measurementData.value, measurementData.unit, effectiveVoltage);
          const currentValue = wattsValue / effectiveVoltage;

          processedData.currentValue = currentValue;
          processedData.currentUnit = 'amps';
          processedData.powerValue = wattsValue;
          processedData.powerUnit = 'watts';

          // Set display values based on selected unit
          if (displayUnit === 'current') {
            processedData.value = currentValue;
            processedData.unit = 'amps';
          } else {
            processedData.value = wattsValue;
            processedData.unit = 'watts';
          }
        } else if (isPowerMeasurement) {
          const apiAdjusted = measurement.adjusted_value != null && measurementData.target_voltage != null;
          const powerValue = apiAdjusted
            ? measurementData.value
            : convertToWatts(measurementData.value, measurementData.unit, effectiveVoltage);
          processedData.powerValue = powerValue;
          processedData.powerUnit = 'watts';
          processedData.currentValue = powerToCurrent(powerValue, effectiveVoltage);
          processedData.currentUnit = 'amps';

          // Set display values based on selected unit
          if (displayUnit === 'current') {
            processedData.value = processedData.currentValue;
            processedData.unit = 'amps';
          } else {
            processedData.value = powerValue;
            processedData.unit = 'watts';
          }
        } else if (measurementData.unit !== 'volts') {
          // Fallback for other relay measurements
          processedData.value = convertToWatts(measurementData.value, measurementData.unit, effectiveVoltage);
          processedData.unit = 'watts';
        }
      } else {
        // Fallback if relay not found — use target_voltage from API or default 120V
        const defaultVoltage = measurementData.target_voltage ?? 120.0;
        processedData.relay = {
          relay_number: relayNumber,
          voltage: defaultVoltage,
          name: t('sites.relayNumber', 'Relay {{number}}', { number: relayNumber }),
        };

        if (measurementData.unit !== 'volts') {
          const apiAdjusted = measurement.adjusted_value != null && measurementData.target_voltage != null;
          processedData.value = apiAdjusted
            ? measurementData.value
            : convertToWatts(measurementData.value, measurementData.unit, defaultVoltage);
          processedData.unit = 'watts';
        }
      }
    } else if (isSensorMeasurement(measurementData.type)) {
      // Handle sensor measurements (temperature, humidity, etc.)
      if (measurementData.type === 'temperature') {
        // Store both Celsius and Fahrenheit values
        const celsiusValue = toCelsius(measurementData.value, measurementData.unit);
        const fahrenheitValue = toFahrenheit(measurementData.value, measurementData.unit);

        processedData.celsiusValue = celsiusValue;
        processedData.celsiusUnit = 'celsius';
        processedData.fahrenheitValue = fahrenheitValue;
        processedData.fahrenheitUnit = 'fahrenheit';

        // Set display values based on selected temperature unit
        if (temperatureUnit === 'fahrenheit') {
          processedData.value = fahrenheitValue;
          processedData.unit = 'fahrenheit';
        } else {
          processedData.value = celsiusValue;
          processedData.unit = 'celsius';
        }

        // Add sensor metadata
        processedData.sensor = {
          sensor_number: parseInt(measurementData.discriminator, 10) || 1,
          name: t('sites.sensorNumber', 'Sensor {{number}}', { number: measurementData.discriminator || 1 }),
        };
      } else {
        // Handle other sensor types (humidity, pressure, etc.)
        processedData.sensor = {
          sensor_number: parseInt(measurementData.discriminator, 10) || 1,
          name: t('sites.sensorNumber', 'Sensor {{number}}', { number: measurementData.discriminator || 1 }),
        };
      }
    }

    return processedData;
  };

  // Group measurements by timestamp for table display
  const groupMeasurementsByTimestamp = measurements => {
    const grouped = {};

    measurements.forEach(measurement => {
      const timestampKey = measurement.datetime_timestamp;

      if (!grouped[timestampKey]) {
        grouped[timestampKey] = {
          timestamp: measurement.timestamp,
          datetime_timestamp: measurement.datetime_timestamp,
          relayMeasurements: {},
          sensorMeasurements: {},
          otherMeasurements: {},
        };
      }

      if (isRelayMeasurement(measurement.type)) {
        // Handle relay measurements (power types)
        if (measurement.relay) {
          grouped[timestampKey].relayMeasurements[measurement.relay.relay_number] = {
            value: typeof measurement.value === 'number' ? measurement.value : parseFloat(measurement.value) || 0,
            unit: measurement.unit,
            relay: measurement.relay,
            target_voltage: measurement.target_voltage,
          };
        }
      } else if (isSensorMeasurement(measurement.type)) {
        // Handle sensor measurements
        if (measurement.sensor) {
          grouped[timestampKey].sensorMeasurements[measurement.sensor.sensor_number] = {
            value: typeof measurement.value === 'number' ? measurement.value : parseFloat(measurement.value) || 0,
            unit: measurement.unit,
            sensor: measurement.sensor,
            type: measurement.type,
            // Include both temperature units if it's a temperature measurement
            celsiusValue: measurement.celsiusValue,
            fahrenheitValue: measurement.fahrenheitValue,
          };
        }
      } else {
        // Handle other measurement types
        const key = measurement.discriminator || 'unknown';
        grouped[timestampKey].otherMeasurements[key] = {
          value: typeof measurement.value === 'number' ? measurement.value : parseFloat(measurement.value) || 0,
          unit: measurement.unit,
          type: measurement.type,
          discriminator: measurement.discriminator,
        };
      }
    });

    return Object.values(grouped).sort((a, b) => b.timestamp - a.timestamp);
  };

  const processedMeasurements = useMemo(() => {
    return measurements
      .filter(measurement => {
        const measurementDate = new Date(measurement.timestamp);
        return isAfter(measurementDate, from) && isBefore(measurementDate, to);
      })
      .map(measurement => processMeasurementData(measurement));
  }, [measurements, from, to, displayUnit, temperatureUnit, relays, timeZone, user]);

  // Group measurements by timestamp for table display
  const groupedDataList = useMemo(() => {
    return groupMeasurementsByTimestamp(processedMeasurements);
  }, [processedMeasurements]);

  // Keep original dataList for chart compatibility - sorted chronologically for time series
  const dataList = useMemo(() => {
    return processedMeasurements.sort((a, b) => a.timestamp - b.timestamp);
  }, [processedMeasurements]);

  // Get unique measurement types for filtering
  // const availableMeasurementTypes = _.uniq(dataList.map(measurement => measurement.type)); // Update query params when 'from' or 'to' changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('from', formatDateForSubmission(from));
    params.set('to', formatDateForSubmission(to));
    const next = `?${params.toString()}`;
    if (next === location.search) return;
    navigate(next, { replace: true });
  }, [from, to, navigate, location.search]);

  // First effect: Scan available measurement types
  useEffect(() => {
    if (unitId) {
      const fromFormatted = formatDateForSubmission(from);
      const toFormatted = formatDateForSubmission(to);

      const scanParams = {
        timestamp__gte: fromFormatted,
        timestamp__lte: toFormatted,
        o: '-timestamp', // Order by newest first
        limit: 10, // Small limit just to scan available types
      };

      fetchMeasurements(unitId, scanParams)
        .then(response => {
          const measurements = response.results || response || [];
          const uniqueTypes = _.uniq(measurements.map(m => m.type)).filter(Boolean);
          setAvailableMeasurementTypes(uniqueTypes);

          // Group measurement types
          const groups = groupMeasurementTypes(uniqueTypes);
          setMeasurementGroups(groups);

          // Set default tab if available - prefer live current or live power
          if (uniqueTypes.length > 0 && !tab) {
            const preferredTypes = ['live_current', 'live_power'];
            let defaultType = null;

            // Look for preferred base types first
            for (const baseType of ['peak', 'live', 'average']) {
              if (groups[baseType]) {
                defaultType = baseType;
                break;
              }
            }

            // Fallback to first available type or group
            if (!defaultType) {
              defaultType = Object.keys(groups)[0] || uniqueTypes[0];
            }

            setTab(defaultType);
          }
        })
        .catch(error => {
          console.error('Error scanning measurement types:', error);
          setAvailableMeasurementTypes([]);
        });
    }
  }, [unitId, from, to]);

  // Second effect: Fetch measurements for selected tab
  useEffect(() => {
    if (unitId && tab && Object.keys(measurementGroups).length > 0) {
      setLoading(true);
      const fromFormatted = formatDateForSubmission(from);
      const toFormatted = formatDateForSubmission(to);

      const selectedGroup = measurementGroups[tab];
      if (!selectedGroup) return;

      // Fetch every measurement type in the group in one request via type__in.
      const typesToFetch = selectedGroup.types;

      const queryParams = {
        timestamp__gte: fromFormatted,
        timestamp__lte: toFormatted,
        limit: MEASUREMENT_PAGE_LIMIT_PER_TYPE * typesToFetch.length,
        o: '-timestamp', // Newest first
        type__in: typesToFetch.join(','),
      };

      fetchMeasurements(unitId, queryParams)
        .then(response => {
          setMeasurements(response.results || response || []);
        })
        .catch(error => {
          console.error('Error fetching measurements:', error);
          setMeasurements([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [unitId, from, to, tab, measurementGroups]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  // Set default groupMode per tab: "byDay" for peak, "all" for others
  useEffect(() => {
    if (tab) {
      setGroupMode(tab === 'peak' ? 'byDay' : 'all');
    }
  }, [tab]);

  const handleDateRangeChange = useCallback((start, end) => {
    if (start) setFrom(start);
    if (end) setTo(end);
  }, []);

  const dateTimeFormat = getDateTimeFormat(user);

  // Data is already filtered by tab selection in the API call
  const filteredDataList = dataList;

  const dateRangeProps = {
    startDate: from,
    endDate: to,
    onDateRangeChange: handleDateRangeChange,
    dateRangeLoading: loading,
    dateRangeExclude: ['last1h', 'last24h'],
    defaultDatePreset: hadInitialDateParams ? 'custom' : 'last30d',
  };

  const tableCommonProps = {
    tab,
    onTabChange: handleTabChange,
    measurementGroups,
    ...dateRangeProps,
  };

  if (dashboardLoading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ pb: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <PowerMenu selectedPage="measurements" site={site.toJS()} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {loading && measurements.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 'var(--radius-lg)' }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 'var(--radius-lg)' }} />
          </Box>
        ) : tab && measurementGroups[tab] ? (
          <>
            {/* Relay Load Info Cards */}
            {isBillboard &&
              measurementGroups[tab] &&
              isRelayMeasurement(measurementGroups[tab].types[0]) &&
              displayRelays.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  {displayRelays.map(dr => (
                    <RelayInfoCard
                      key={dr.key}
                      dr={dr}
                      relayInfo={relayInfo}
                      expectedReadings={expectedReadings}
                      dispatch={dispatch}
                      siteId={site.get('id')}
                      fixtures={fixtures}
                      isBillboard={isBillboard}
                    />
                  ))}
                </Box>
              )}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 'var(--radius-lg)' }}>
              <MeasurementChart
                key={`chart-${tab}-${temperatureUnit}`}
                list={filteredDataList}
                measurementType={tab}
                availableTypes={[]}
                expectedReadings={expectedReadings}
                displayRelays={displayRelays}
                relayInfo={relayInfo}
                displayUnit={displayUnit}
                groupMode={groupMode}
                timeZone={timeZone}
              />
            </Paper>

            {measurementGroups[tab] && isRelayMeasurement(measurementGroups[tab].types[0]) ? (
              <MeasurementDataTable
                key={`table-${tab}-${displayUnit}`}
                dateTimeFormat={dateTimeFormat}
                data={groupedDataList}
                relays={relays}
                relayInfo={relayInfo}
                displayRelays={displayRelays}
                expectedReadings={expectedReadings}
                displayUnit={displayUnit}
                onDisplayUnitChange={setDisplayUnit}
                showUnitToggle
                timeZone={timeZone}
                groupMode={groupMode}
                onGroupModeChange={setGroupMode}
                {...tableCommonProps}
              />
            ) : measurementGroups[tab] && isSensorMeasurement(measurementGroups[tab].types[0]) ? (
              <MeasurementDataTable
                key={`table-${tab}-${temperatureUnit}`}
                dateTimeFormat={dateTimeFormat}
                data={groupedDataList}
                sensors={true}
                timeZone={timeZone}
                groupMode={groupMode}
                onGroupModeChange={setGroupMode}
                {...tableCommonProps}
              />
            ) : (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 'var(--radius-md)' }}>
                <Typography variant="h6" gutterBottom>
                  {getMeasurementTypeLabel(tab, t)} {t('measurements.data')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('measurements.nonRelayNotSupported')}
                </Typography>
              </Box>
            )}
          </>
        ) : Object.keys(measurementGroups).length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 'var(--radius-lg)', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {t('measurements.noMeasurementsAvailable')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('measurements.noMeasurementsInRange')}
            </Typography>
          </Paper>
        ) : null}
      </Box>
    </Box>
  );
}

export default MeasurementDataView;
